import { useProcedureDetailsQuery } from '@/hooks/useDatabase'
import type { DatabaseConnection } from '@/stores/connectionStore'
import { CogIcon, ClipboardDocumentIcon, ChatBubbleLeftIcon, ArrowUturnLeftIcon, PlayIcon } from '@heroicons/react/24/outline'
import { useState, useCallback } from 'react'
import { EditProcedurePanel } from './EditProcedurePanel'
import { generateProcedureCall, navigateToQueryEditor } from '@/utils/functionExecution'

// API response interface matching the actual backend response
export interface ProcedureDetailsResponse {
  command_id?: string;
  definition?: string;
  metadata?: {
    procedure_name?: string;
    database_name?: string;
    schema_name?: string;
    routine_type?: string;
    created_date?: string;
    modified_date?: string;
    description?: string;
    full_source?: boolean;
    source?: string;
    parameters?: {
      column_name?: string;
      data_type?: string;
      column_type?: 'IN' | 'OUT' | 'INOUT';
      full_data_type?: string;
      number?: number;
      nullable?: boolean;
      precision?: number | null;
      scale?: number | null;
    }[];
  };
  timestamp?: number;
}

interface ProcedureDetailsViewProps {
  connection: DatabaseConnection
  database: string
  procedureName: string
  schema?: string
  openEditPanel?: boolean
  onEditPanelClose?: () => void
}

export function ProcedureDetailsView({ connection, database, procedureName, schema, openEditPanel, onEditPanelClose }: ProcedureDetailsViewProps) {
  const [copiedSection, setCopiedSection] = useState<string | null>(null)

  const handlePanelSuccess = useCallback(() => {
    // The EditProcedurePanel already invalidates the procedure-details query,
    // so we don't need to do anything here. The view will automatically update.
  }, [])

  const { data: procedureDetailsResponse, isLoading, error } = useProcedureDetailsQuery(
    connection.id,
    database,
    procedureName,
    schema,
    { enabled: true, databaseType: connection.type }
  )

  // Narrow to the API response type safely
  const procedureDetails: ProcedureDetailsResponse | undefined = (
    procedureDetailsResponse && typeof procedureDetailsResponse === 'object'
  ) ? (procedureDetailsResponse as ProcedureDetailsResponse) : undefined

  const copyToClipboard = async (text: string, section: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedSection(section)
      setTimeout(() => { setCopiedSection(null) }, 2000)
    } catch (err) {
      console.error('Failed to copy text: ', err)
    }
  }

  const handleExecuteProcedure = useCallback(() => {
    if (!procedureName) return
    
    try {
      // Build arguments string from parameters if available
      const argumentsString = procedureDetails?.metadata?.parameters
        ?.map((param) => {
          const mode = param.column_type || 'IN'
          const name = param.column_name || 'param'
          const type = param.full_data_type || param.data_type || 'unknown'
          return `${mode} ${name} ${type}`
        })
        .join(', ')
      
      // Generate the procedure call query
      const query = generateProcedureCall(
        procedureName,
        argumentsString,
        (connection.type === 'postgresql' ? 'postgresql' : 'mysql'),
        schema,
        database
      )
      
      // Navigate to query editor with the pre-filled query
      navigateToQueryEditor(connection, query, database, schema, true)
    } catch (error) {
      console.error('Error generating procedure call:', error)
    }
  }, [procedureName, procedureDetails, connection, database, schema])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">
            Error loading procedure details: {error.message || 'Unknown error'}
          </p>
        </div>
      </div>
    )
  }

  if (!procedureDetails) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <p className="text-yellow-800 dark:text-yellow-200">
            No procedure details found
          </p>
        </div>
      </div>
    )
  }

  return (
    <>
    <div className="h-full flex flex-col bg-white dark:bg-gray-800">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <CogIcon className="w-8 h-8 mr-3 text-orange-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {procedureDetails.metadata?.procedure_name || procedureName}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {procedureDetails.metadata?.database_name || database}{procedureDetails.metadata?.schema_name && procedureDetails.metadata.schema_name !== 'public' && procedureDetails.metadata.schema_name !== database ? `.${procedureDetails.metadata.schema_name}` : ''} • {procedureDetails.metadata?.routine_type || 'Stored Procedure'}
              </p>
            </div>
          </div>
          <button type="button"
            onClick={handleExecuteProcedure}
            className="flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
            title="Execute procedure in Query Editor"
          >
            <PlayIcon className="w-5 h-5 mr-2" />
            Execute Procedure
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-600 dark:text-blue-400 text-sm font-medium">Return Type</p>
                <p className="text-blue-900 dark:text-blue-100 text-lg font-semibold">
                  VOID
                </p>
              </div>
              <ArrowUturnLeftIcon className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-600 dark:text-green-400 text-sm font-medium">Parameters</p>
                <p className="text-green-900 dark:text-green-100 text-lg font-semibold">
                  {procedureDetails.metadata?.parameters?.length || 0}
                </p>
              </div>
              <CogIcon className="w-8 h-8 text-green-500" />
            </div>
          </div>
          
          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-600 dark:text-purple-400 text-sm font-medium">Definition Length</p>
                <p className="text-purple-900 dark:text-purple-100 text-lg font-semibold">
                  {procedureDetails.definition ? `${Math.round(procedureDetails.definition.length / 100) / 10}K chars` : 'N/A'}
                </p>
              </div>
              <ClipboardDocumentIcon className="w-8 h-8 text-purple-500" />
            </div>
          </div>
        </div>

        {/* Basic Information */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Basic Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Name
              </label>
              <p className="text-sm text-gray-900 dark:text-white mt-1">
                {procedureDetails.metadata?.procedure_name || 'N/A'}
              </p>
            </div>
            
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Schema
              </label>
              <p className="text-sm text-gray-900 dark:text-white mt-1">
                {procedureDetails.metadata?.schema_name || 'N/A'}
              </p>
            </div>
            
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Return Type
              </label>
              <div className="flex items-center mt-1">
                <ArrowUturnLeftIcon className="w-4 h-4 mr-1 text-gray-500" />
                <code className="text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-gray-900 dark:text-white">
                  VOID
                </code>
              </div>
            </div>
            
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Language
              </label>
              <p className="text-sm text-gray-900 dark:text-white mt-1">
                SQL
              </p>
            </div>
            
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Routine Type
              </label>
              <p className="text-sm text-gray-900 dark:text-white mt-1">
                {procedureDetails.metadata?.routine_type || 'PROCEDURE'}
              </p>
            </div>
            
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Created Date
              </label>
              <p className="text-sm text-gray-900 dark:text-white mt-1">
                {procedureDetails.metadata?.created_date ? new Date(procedureDetails.metadata.created_date).toLocaleDateString() : 'N/A'}
              </p>
            </div>
            
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Modified Date
              </label>
              <p className="text-sm text-gray-900 dark:text-white mt-1">
                {procedureDetails.metadata?.modified_date ? new Date(procedureDetails.metadata.modified_date).toLocaleDateString() : 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* Parameters */}
        {procedureDetails.metadata?.parameters && procedureDetails.metadata.parameters.length > 0 && (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Parameters ({procedureDetails.metadata.parameters.length})
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Position
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Mode
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Length
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Precision/Scale
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Nullable
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                  {procedureDetails.metadata.parameters
                    .sort((a, b) => (a.number || 0) - (b.number || 0))
                    .map((param, index) => (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                        <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-blue-500 rounded-full">
                          {param.number || index + 1}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-xs">
                          {param.column_name || `param_${index + 1}`}
                        </code>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                        <code className="bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 px-2 py-1 rounded text-xs font-medium">
                          {param.full_data_type || param.data_type || 'unknown'}
                        </code>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          param.column_type === 'IN' 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : param.column_type === 'OUT'
                            ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                            : param.column_type === 'INOUT'
                            ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                        }`}>
                          {param.column_type || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                        <span className="text-gray-400">-</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                        {param.precision !== undefined ? (
                          <span className="inline-flex items-center px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 rounded">
                            {param.precision}
                            {param.scale !== undefined && param.scale !== null && param.scale > 0 && `,${param.scale}`}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                        {param.nullable === true ? (
                          <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 rounded-full">
                            Yes
                          </span>
                        ) : param.nullable === false ? (
                          <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded-full">
                            No
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Referenced Tables */}
        {procedureDetails.definition && (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Referenced Tables
            </h3>
            {(() => {
              // Extract table names from SQL (basic regex - could be enhanced)
              const sqlText = procedureDetails.definition.toUpperCase();
              const tableMatches = sqlText.match(/(?:FROM|JOIN|UPDATE|INTO)\s+`?([a-zA-Z_][a-zA-Z0-9_]*)`?/gi);
              const tables = tableMatches 
                ? Array.from(new Set(tableMatches.map(match => 
                    match.replace(/(?:FROM|JOIN|UPDATE|INTO)\s+`?/i, '').replace(/`$/, '').toLowerCase()
                  )))
                : [];
              
              return tables.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {tables.map((table, index) => (
                    <span 
                      key={index}
                      className="inline-flex items-center px-3 py-1 text-sm bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 rounded-full border border-blue-200 dark:border-blue-800"
                    >
                      <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                      {table}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  No table references found in the procedure definition.
                </p>
              );
            })()}
          </div>
        )}

        {/* SQL Operations Analysis */}
        {procedureDetails.definition && (
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              SQL Operations Analysis
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {['SELECT', 'UPDATE', 'INSERT', 'DELETE'].map((operation, index) => {
                const count = (procedureDetails.definition?.toUpperCase().match(new RegExp(`\\b${operation}\\b`, 'g')) || []).length;
                return (
                  <div key={`sql-op-${operation}-${index}`} className="text-center">
                    <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full text-white font-bold ${
                      operation === 'SELECT' ? 'bg-blue-500' :
                      operation === 'UPDATE' ? 'bg-yellow-500' :
                      operation === 'INSERT' ? 'bg-green-500' : 'bg-red-500'
                    }`}>
                      {count}
                    </div>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{operation}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Description */}
        {procedureDetails.metadata?.description && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-center mb-2">
              <ChatBubbleLeftIcon className="w-5 h-5 mr-2 text-blue-600" />
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                Description
              </h3>
            </div>
            <p className="text-blue-800 dark:text-blue-200 whitespace-pre-wrap">
              {procedureDetails.metadata.description}
            </p>
          </div>
        )}

        {/* Definition */}
        {procedureDetails.definition && (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Procedure Definition
              </h3>
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {procedureDetails.definition.split('\n').length} lines • {procedureDetails.definition.length} characters
                </span>
                <button type="button"
                  onClick={() => void copyToClipboard(procedureDetails.definition!, 'definition')}
                  className="flex items-center px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md transition-colors"
                >
                  <ClipboardDocumentIcon className="w-4 h-4 mr-1" />
                  {copiedSection === 'definition' ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900 rounded-md p-4 overflow-x-auto border border-gray-200 dark:border-gray-700">
              <pre className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap font-mono leading-relaxed">
                {procedureDetails.definition}
              </pre>
            </div>
            
            {/* Definition Metadata */}
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Lines:</span>
                  <span className="ml-2 font-medium text-gray-900 dark:text-white">
                    {procedureDetails.definition.split('\n').length}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Characters:</span>
                  <span className="ml-2 font-medium text-gray-900 dark:text-white">
                    {procedureDetails.definition.length}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Words:</span>
                  <span className="ml-2 font-medium text-gray-900 dark:text-white">
                    {procedureDetails.definition.split(/\s+/).filter(word => word.length > 0).length}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Size:</span>
                  <span className="ml-2 font-medium text-gray-900 dark:text-white">
                    {(new Blob([procedureDetails.definition]).size / 1024).toFixed(1)} KB
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>

    {/* Edit Procedure Panel */}
    {openEditPanel && onEditPanelClose && (
      <EditProcedurePanel
        connection={connection}
        database={database}
        procedureName={procedureName}
        schema={schema}
        initialProcedure={procedureDetails}
        onClose={onEditPanelClose}
        onSuccess={handlePanelSuccess}
      />
    )}
  </>
  )
}
